import { Injectable } from '@angular/core';
import { WizardPage } from '@app/workflow/components/navigator/wizard-view/interfaces';
import { QuestionStatusService } from '@app/pages/questions/question-status.service';
import { MessplatzService } from '@app/services/messplatz/messplatz.service';
import { INavTreeNode } from '@app/workflow/components/navigator/nav-tree/interfaces';
import { NavTreeNode, WizardPageTreeNode } from '@app/workflow/components/navigator/nav-tree/nav-tree-nodes';
import { SpecialQuestionService } from '@app/workflow/components/questionnaire/questionnaire-item/special-question/special-question.service';
import { SessionItemData } from '@app/workflow/services/session-item-data';
import { cast, IQuestionnaireGroup, IQuestionnaireItem, IQuestionnaireWorkflowItem, IWorkflowItem } from '@interfaces/interfaces';
import { BlockQuestion, BlockQuestionItem, SpecialQuestionItem } from '@interfaces/questions';
import { cloneDeep, isNil } from 'lodash';
import {MissingValueService} from '@app/services/missing-value/missing-value.service';
import {
	IWizardMissingValueData
} from '@workflow/components/navigator/missing-value-button/wizard-missing-value-button.component';

/**
 * Erzeugt einen Baum zur Navigation durch durch das WorkflowItem
 */
@Injectable({
	providedIn: 'root',
})
export class NavTreeBuilder {

	private static getAllMissingValueKeysOfGroup(group: IQuestionnaireGroup): IWizardMissingValueData[] | null {
		const missingKeys: IWizardMissingValueData[] = group.items?.filter(i => i.missingValueKey).map(i => {
			return {key: i.missingValueKey, title: i.title};
		});
		if (missingKeys.length) {
			return missingKeys;
		}

		return null;
	}

	constructor(
		private specialQuestionService: SpecialQuestionService,
		private messplatzService: MessplatzService,
		private questionStatusService: QuestionStatusService,
	) {}

	buildNavTree(workflowItem: IWorkflowItem): INavTreeNode {
		if (!workflowItem) {
			throw Error('undefined workflowItem');
		}
		return this.buildWorkflowItem(workflowItem);
	}

	private buildWorkflowItem(workflowItem: IWorkflowItem): INavTreeNode {
		const navGroups = this.buildGroups(workflowItem);

		const root = new NavTreeNode(['']);
		root.setChildren(navGroups);

		return root;
	}

	private buildGroups(workflowItem: IWorkflowItem): Array<INavTreeNode> {
		if (workflowItem.type !== 'Questionnaire') {
			return [];
		}

		const questionnaire = workflowItem as IQuestionnaireWorkflowItem;
		const navGroups = questionnaire.groups.map((group, index) => {
			return this.buildGroup(group, '/' + index);
		});

		return navGroups;
	}

	private isGroupProcedure(group: IQuestionnaireGroup): boolean {
		return group.isProcedure || group.items.some(i => i.isProcedure);
	}

	private buildGroup(group: IQuestionnaireGroup, urlPrefix: string): INavTreeNode {

		const nodes = new Array<INavTreeNode>();

		const prefaces = group.preface || [];
		const epilogs = group.epilog || [];
		const items = group.items || [];

		// TODO: GSP-363 SpecialQuestions sind eigentlich alles Prozeduren. Das wird aber nicht nochmal explizit
		// in der Konfiguration eingetragen
		for (const item of items) {
			if (this.specialQuestionService.isSpecialQuestion(item.type)) {
				item.isProcedure = true;
			}
		}

		// Gruppen-Prologe erstellen
		for (const [index, preface] of prefaces.entries()) {
			nodes.push(this.buildProlog(group, preface, urlPrefix + '/p/' + index));
		}

		// IQuestionaireItems erstellen
		for (const [index, item] of items.entries()) {
			nodes.push(this.buildItem(group, item, urlPrefix + '/i/' + index));
		}

		// Gruppen-Epiloge erstellen
		for (const [index, epilog] of epilogs.entries()) {
			nodes.push(this.buildEpilog(group, epilog, urlPrefix + '/e/' + index));
		}

		const groupNode = new NavTreeNode([urlPrefix]);

		groupNode.isEnabled = (data) => this.questionStatusService.checkGroupEnabled(group, data);
		groupNode.setChildren(nodes);
		groupNode.isQuestionGroup = true;
		groupNode.isProcedure = this.isGroupProcedure(group);

		return groupNode;
	}

	private buildProlog(group: IQuestionnaireGroup, preface: string, urlPrefix: string, fullHeight?: boolean): WizardPageTreeNode {
		const page: WizardPage = {
			...group,
			question: {
				type: 'prolog',
				title: group.title,
				description: preface,
				centered: !preface.includes('<img src=') && !preface.includes('<table>'),
			},
			obligatoryUnskippableKeys: [],
			obligatoryAllKindOfKeys: [],
			isProcedure: this.isGroupProcedure(group),
			fullHeight: fullHeight,
		};
		if (!page.missingValueKey && !page.missingValueKeys) {
			page.missingValueKeys = NavTreeBuilder.getAllMissingValueKeysOfGroup(group);
		}

		const node = new WizardPageTreeNode([urlPrefix], page);
		node.isProlog = true;
		node.isProcedure = page.isProcedure;

		return node;
	}

	private buildEpilog(group: IQuestionnaireGroup, epilog: string, urlPrefix: string): INavTreeNode {
		const page: WizardPage = {
			...group,
			question: {
				type: 'epilog',
				title: group.title,
				description: epilog,
				centered: !epilog.includes('<img src=') && !epilog.includes('<table>'),
			},
			obligatoryUnskippableKeys: [],
			obligatoryAllKindOfKeys: [],
			isProcedure: this.isGroupProcedure(group),
		};
		const node = new WizardPageTreeNode([urlPrefix], page);
		node.isEpilog = true;
		node.isProcedure = page.isProcedure;
		return node;
	}

	private buildItem(group: IQuestionnaireGroup, item: IQuestionnaireItem, urlPrefix: string, missingDevices?: string[]): INavTreeNode {
		if (item.type === 'BlockQuestion') {
			return this.buildBlockQuestionItem(group, item as BlockQuestionItem, urlPrefix, missingDevices);
		}

		if (this.specialQuestionService.isSpecialQuestion(item.type)) {
			return this.buildSpecialQuestionItem(group, item as SpecialQuestionItem, urlPrefix);
		}

		// Fallback
		return this.buildProlog(group, `Test "${item.type}" ist nicht bekannt`, urlPrefix);
	}

	private buildBlockQuestionItemPage(group: IQuestionnaireGroup, blockQuestion: BlockQuestionItem, questions: BlockQuestion[], urls: string[], missingDevices: string[]): INavTreeNode {
		const q: BlockQuestionItem = {
			...blockQuestion,
			items: questions,
		};
		const isWizardSkipAllowed = q.items?.every(question => question?.question?.isWizardSkipAllowed);
		const page: WizardPage = {
			...group,
			question: q,
			obligatoryUnskippableKeys: [],
			obligatoryAllKindOfKeys: [],
			alignBottom: blockQuestion.alignBottom,
			layout: blockQuestion.options?.wizard?.layout || 'horizontal',
			isProcedure: blockQuestion.isProcedure || group.isProcedure,
			missingDevices: missingDevices,
			isWizardSkipAllowed: isWizardSkipAllowed,
			missingValueKey: blockQuestion.missingValueKey,
			missingValueKeyGroup: group.missingValueKey,
		};

		for (const block of questions) {
			const question = block.question || blockQuestion.default;

			if (!question?.isAnswerOptional) {
				page.obligatoryAllKindOfKeys.push(block.key);

				if (!question?.isWizardSkipAllowed) {
					page.obligatoryUnskippableKeys.push(block.key);
				}
			}
		}

		const node: INavTreeNode = new WizardPageTreeNode(urls, page);

		if (missingDevices) {
			node.isEnabled = (data: SessionItemData) => questions.some(question => !question.showIf || !!data.getProperty(question.showIf))
					&& !MissingValueService.isMissingValue(group, data) && !MissingValueService.isMissingValue(blockQuestion, data);
		} else {
			node.isEnabled = (data: SessionItemData) => questions.some(question => !question.showIf || !!data.getProperty(question.showIf));
		}
		node.isCompleted = (data: SessionItemData) => this.questionStatusService.checkQuestionGroupStatus(group, data, [page.question]).status === 'finished';
		node.isProcedure = page.isProcedure;
		return node;
	}

	/**
	 * BlockQuestionItem verarbeiten
	 * @param group Fragegruppe
	 * @param block Frageblock
	 * @param urlPrefix URL-Prefix
	 * @param missingDevices optionale Liste an fehlenden Devices. Die Option wird nur zur Anzeige in der alternativen Eingabe benötigt
	 */
	private buildBlockQuestionItem(group: IQuestionnaireGroup, block: BlockQuestionItem, urlPrefix: string, missingDevices: string[]): INavTreeNode {

		const prefaces = block.preface || [];

		const nodes = new Array<INavTreeNode>();

		// Fragen-Prologe erstellen
		for (const [index, preface] of prefaces.entries()) {
			nodes.push(this.buildProlog(group, preface, urlPrefix + '/p/' + index));
		}

		if (!block.options?.wizard?.splitBlock) {
			// Block auf einer Seite zusammenhalten
			const urls: string[] = [];
			for (const [index] of block.items.entries()) {
				urls.push(urlPrefix + '/q/' + index);
			}
			nodes.push(this.buildBlockQuestionItemPage(group, block, block.items, urls, missingDevices));
		} else {
			// Block auf mehrere Seiten verteilen
			let keepAsBlock = new Array<[BlockQuestion, string]>();
			for (const [index, blockQuestion] of block.items.entries()) {
				const url = urlPrefix + '/q/' + index;
				// aufeinanderfolgende Einzelfragen mit "keepAsBlock" werden trotzdem zusammengefasst
				if (blockQuestion.keepAsBlock) {
					keepAsBlock.push([blockQuestion, url]);

					// Die nächste Frage steht wieder alleine. Alle bisherigen keepAsBlock Fragen als eine Seite ausgeben
					if (!block.items[index + 1]?.keepAsBlock) {
						const questions = keepAsBlock.map(tuple => tuple[0]);
						const urls = keepAsBlock.map(tuple => tuple[1]);

						nodes.push(this.buildBlockQuestionItemPage(group, block, questions, urls, missingDevices));
						keepAsBlock = [];
						continue;
					}
				} else {
					// Frage alleine auf einer Seite ausgeben
					nodes.push(this.buildBlockQuestionItemPage(group, block, [blockQuestion], [url], missingDevices));
					continue;
				}
			}
		}

		const node = new NavTreeNode([urlPrefix]);
		node.setChildren(nodes);
		node.isProcedure = this.isGroupProcedure(group);
		node.isEnabled = (data: SessionItemData) => !block.showIf || !!data.getProperty(block.showIf);
		return node;
	}

	private buildSpecialQuestionItem(group: IQuestionnaireGroup, specialQuestion: SpecialQuestionItem, urlPrefix: string): INavTreeNode {

		const specialQuestionImpl = this.specialQuestionService.getSpecialQuestion(specialQuestion);
		const nodes = new Array<INavTreeNode>();

		if (!isNil(specialQuestionImpl.defaultGroupConfiguration.startText)) {
			group = cloneDeep(group);
			group.startText = specialQuestionImpl.defaultGroupConfiguration.startText;
		}
		const wizardPages = specialQuestionImpl.buildWizardPages(group, specialQuestion, 0, {});
		let missingValueKeys: { title: string; key: string }[];
		const baseKey = specialQuestion.baseKey || specialQuestion.key;
		if (!specialQuestionImpl.missingValueKeyGroup){
			missingValueKeys = wizardPages.filter(page => page.missingValueKey).map(page => ({
				key: `${baseKey}.${page.missingValueKey}`,
				title: page.title,
			}));
			if (!missingValueKeys.length) {
				missingValueKeys = null;
			}
		} else {
			missingValueKeys = [{key: `${baseKey}.${specialQuestionImpl.missingValueKeyGroup}`, title: specialQuestionImpl.title}];
		}

		//Build Prolog
		const prefaces = specialQuestionImpl.defaultGroupConfiguration.preface || [];
		for (const [index, preface] of prefaces.entries()) {
			const node = this.buildProlog(group, preface, urlPrefix + '/p/' + index, specialQuestionImpl.prologueFullHeight);
			node.requiredDevices = specialQuestionImpl.requiredDevices;
			node.page.alignBottom = true;
			node.page.missingValueKeys = missingValueKeys;
			nodes.push(node);
		}

		//Build content
		for (const [index, wizardPage] of wizardPages.entries()) {
			if (specialQuestionImpl.missingValueKeyGroup) {
				wizardPage.missingValueKey = `${baseKey}.${specialQuestionImpl.missingValueKeyGroup}`;
			} else if (wizardPage.missingValueKey) {
				wizardPage.missingValueKey = `${baseKey}.${wizardPage.missingValueKey}`;
			}
			const node = new WizardPageTreeNode([urlPrefix + '/q/' + index], wizardPage);

			const showIf = (data: SessionItemData) => !wizardPage.question.showIf || !!data.getProperty(wizardPage.question.showIf);
			const hasDevices = () => this.messplatzService.hasAllDevices(specialQuestionImpl.requiredDevices);
			node.isEnabled = (data) => hasDevices() && showIf(data) && !MissingValueService.isMissingValue(group, data) && !MissingValueService.isMissingValue(wizardPage, data);
			node.isCompleted = (data) =>  MissingValueService.isMissingValue(group, data) || MissingValueService.isMissingValue(wizardPage, data)
					|| specialQuestionImpl.isCompleted(data.getProperty(specialQuestion.key));
			node.isProcedure = specialQuestion.isProcedure || group.isProcedure;
			nodes.push(node);
		}

		//Alternative Eingabe Seiten
		const alternateInputGroup = [...specialQuestionImpl.defaultGroupConfiguration.items];
		for (const [index, item] of alternateInputGroup.entries()) {
			let copy = item;
			if (item.type === 'BlockQuestion') {
				const bq = item as BlockQuestionItem;
				copy = cast<BlockQuestionItem>({
					...bq,
					items: bq.items.map(q => ({...q, key: specialQuestion.key + '.' + q.key})),
				});
			}
			if (specialQuestionImpl.missingValueKeyGroup) {
				copy.missingValueKey = `${baseKey}.${specialQuestionImpl.missingValueKeyGroup}`;
			} else if (item.missingValueKey) {
				copy.missingValueKey = `${baseKey}.${item.missingValueKey}`;
			}
			// Die alternative Eingabe wird nur angezeigt, wenn die requiredDevices fehlen. Daher werden die
			// requiredDevices hier fest als missingDevices eingetragen
			const missingDevices = this.messplatzService.getMissingDevicesNames(specialQuestionImpl.requiredDevices);
			const node = this.buildItem(group, copy, urlPrefix + '/a/' + index, missingDevices);
			node.isEnabled = () => !this.messplatzService.hasAllDevices(specialQuestionImpl.requiredDevices);
			nodes.push(node);
		}

		//Build Epilog
		const epilogs = specialQuestionImpl.buildEpilogPages(group, specialQuestion);
		for (const [index, epilog] of epilogs.entries()) {
			if (specialQuestionImpl.missingValueKeyGroup) {
				epilog.missingValueKey = `${baseKey}.${specialQuestionImpl.missingValueKeyGroup}`;
			} else if (epilog.missingValueKey) {
				epilog.missingValueKey = `${baseKey}.${epilog.missingValueKey}`;
			}
			const node = new WizardPageTreeNode([urlPrefix + '/e/' + index], epilog);
			node.isEpilog = true;
			node.isProcedure = this.isGroupProcedure(group);
			nodes.push(node);
		}

		const node = new NavTreeNode([urlPrefix]);
		node.setChildren(nodes);
		node.isProcedure = this.isGroupProcedure(group);
		node.isEnabled = (data: SessionItemData) => !specialQuestion.showIf || !!data.getProperty(specialQuestion.showIf);

		return node;
	}
}
